import { useEffect, useRef, useState } from "react";
import type { Api } from "../api.ts";
import { getPricing } from "../data/money.ts";
import { freeIntro } from "../data/plan.ts";
import { studioStartLine } from "../signup.ts";
import type { Workspace } from "../types.ts";
import { errText } from "./app.ts";
import { Icon } from "./icons.tsx";

/** Signed in, member of nothing yet: start a workspace (you become its owner), or open an invitation link. */
export function FirstWorkspace({
  api,
  onCreated,
}: {
  api: Pick<Api, "request">;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // the workspace is made and the access code put credits on it: say so before going in
  const [made, setMade] = useState<{
    id: string;
    name: string;
    credits: number;
    creditEur: number;
    free: boolean;
  } | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);
  if (made)
    return (
      <div className="empty-state first-workspace">
        <Icon name="folder" size={28} />
        <h2>{made.name} is ready</h2>
        <p role="status">{studioStartLine(made.credits, made.creditEur)}</p>
        <p className="muted small">
          Credits pay for the stills and videos you make. They are yours to spend.
        </p>
        {made.free && <p className="muted small">{freeIntro(getPricing().hosted)}</p>}
        <button
          ref={(el) => el?.focus()}
          type="button"
          className="btn primary"
          onClick={() => onCreated(made.id)}
        >
          Go to your studio
        </button>
      </div>
    );
  return (
    <div className="empty-state first-workspace">
      <Icon name="folder" size={28} />
      <h2>Create your workspace</h2>
      <p>
        A workspace is your studio: its productions, crew and costs live there. You become its owner and can
        invite others.
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          setErr(null);
          try {
            const w = await api.request<Workspace & { starter_credits?: number }>("POST", "/workspaces", {
              body: { name: name.trim() },
            });
            const credits = w.starter_credits ?? 0;
            if (credits <= 0) return onCreated(w.id);
            // a free workspace has its limits; say them once, here, instead of at the first refusal
            const free = await api
              .request<{ plan?: string | null }>("GET", `/workspaces/${w.id}/plan`)
              .then((p) => p.plan === "free")
              .catch(() => false);
            // the price table is the server's; the launch price when it cannot be read
            const creditEur = await api
              .request<{ credit_eur?: number }>("GET", "/pricing")
              .then((p) => (typeof p.credit_eur === "number" && p.credit_eur > 0 ? p.credit_eur : 0.05))
              .catch(() => 0.05);
            setMade({ id: w.id, name: w.name, credits, creditEur, free });
          } catch (x) {
            setErr(errText(x));
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span>Workspace name</span>
          <input
            ref={first}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Studio name"
            maxLength={80}
            required
          />
        </label>
        {err && (
          <p className="error-text" role="alert">
            {err}
          </p>
        )}
        <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
          {busy ? "Creating…" : "Create workspace"}
        </button>
      </form>
      <p className="muted small">Joining a team instead? Open the invitation link you were sent.</p>
    </div>
  );
}
